import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import { useParams, useLocation } from "react-router-dom";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard/UnsavedChangesGuard";
import Cart from "./Cart";
import AppBarComp from "./AppBarComp";
import FabButton from "../components/Fab/Fab";
import PaymentDetails from "./PaymentDetails";
import { useCurrency } from "../Context/CurrencyContext";
import { useCart } from "../Context/CartContext";
import { useDiscount } from "../Context/DiscountContext";
import { useOrderSession } from "../Context/OrderSessionContext";
import { formatInvoiceNumber } from "../utils/docNumber";
import ItemCards from "./ItemCards";
import FloatingFooter from "./FloatingFooter";
import ShiftStartDialog from "./ShiftStartDialog";
import { CART_BREAKPOINT, APP_HEADER_HEIGHT, APP_FOOTER_HEIGHT, APP_FOOTER_HEIGHT_XS, POS_CATEGORY_BAR_HEIGHT, POS_CATEGORY_BAR_HEIGHT_XS } from "./cartLayout";
import { GRID_LAYOUTS } from "./GridLayoutSelector";
import {
  createInvoice,
  getInvoice,
  updateInvoice,
  getInvoices,
  clearInvoice
} from "../actions/invoiceActions";
import { getStaffCommissionDetail } from "../actions/staffCommissionDetailActions";

const POS_SHELL = APP_HEADER_HEIGHT + APP_FOOTER_HEIGHT;
const POS_SHELL_XS = APP_HEADER_HEIGHT + 40;
const POS_SHELL_WITH_CATEGORIES = APP_HEADER_HEIGHT + APP_FOOTER_HEIGHT + POS_CATEGORY_BAR_HEIGHT;
const POS_SHELL_WITH_CATEGORIES_XS = APP_HEADER_HEIGHT + APP_FOOTER_HEIGHT_XS + POS_CATEGORY_BAR_HEIGHT_XS;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    // The app shell already reserves space for the fixed header and footer.
    // Using 100vh here created an empty, always-scrollable strip on POS.
    minHeight: `calc(100vh - ${POS_SHELL}px)`,
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      minHeight: `calc(100vh - ${POS_SHELL_XS}px)`,
    },
  },
  rootWithCategories: {
    minHeight: `calc(100vh - ${POS_SHELL_WITH_CATEGORIES}px)`,
    [theme.breakpoints.down("xs")]: {
      minHeight: `calc(100vh - ${POS_SHELL_WITH_CATEGORIES_XS}px)`,
    },
  },

  emptyCart: {
    textAlign: "center",
  },
  swipeContainer: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  payNowButton: {
    margin: theme.spacing(2),
  },
  swipeableItem: {
    transition: "transform 0.3s ease, background-color 0.3s ease",
    "&.swiped": {
      backgroundColor: "red",
      transform: "translateX(-100%)",
    },
  },
}));

// isActive: true whenever this is the currently-matched route. PosMainScreen
// now stays mounted across navigations (see PersistentPosMainScreen.js)
// instead of unmounting/remounting on every visit, so isActive replaces
// "just mounted" as the signal for "refresh my data" / "listen for input" -
// see the effect below and ItemCards' barcode-scanner listener.
function PosMainScreen({ isActive = true }) {
  const classes = useStyles();
  const theme = useTheme();
  const isCartPersistent = useMediaQuery(theme.breakpoints.up(CART_BREAKPOINT));

  const dispatch = useDispatch();
  const { currency, updateCurrency } = useCurrency();
  const { id } = useParams();
  const location = useLocation();
  // The payment screen is a real route (/PosMainScreen/payment or
  // /edit/invoice/:id/payment) rather than a separate boolean, so there's a
  // single source of truth for "am I on payment" - no risk of it drifting
  // out of sync with the URL the way a parallel context flag could.
  const isPaymentView = location.pathname.endsWith("/payment");
  // Cart starts open on desktop (persistent sidebar); on mobile/tablet it's
  // a full-width overlay, so it should start closed until the user taps it.
  const [open, setOpen] = useState(isCartPersistent);
  // User-selectable card grid layout on desktop (default 5x4).
  // Auto-hidden on mobile where the 2-column mobile layout is always preserved.
  const [gridLayout, setGridLayout] = useState(() => {
    try {
      const savedLayout = localStorage.getItem("pos_item_grid_layout");
      const migratedDefault = localStorage.getItem("pos_item_grid_layout_v2") === "true";
      // Move the prior app default to the new 5x4 default once. A later
      // explicit selection of 6x5 is preserved because the marker is set.
      if (savedLayout === "6x5" && !migratedDefault) {
        localStorage.setItem("pos_item_grid_layout", "5x4");
        localStorage.setItem("pos_item_grid_layout_v2", "true");
        return "5x4";
      }
      return savedLayout || "5x4";
    } catch (e) {
      return "5x4";
    }
  });

  const handleGridLayoutChange = (newLayout) => {
    setGridLayout(newLayout);
    try {
      localStorage.setItem("pos_item_grid_layout", newLayout);
      localStorage.setItem("pos_item_grid_layout_v2", "true");
    } catch (e) {}
  };

  const currentGridCols =
    (GRID_LAYOUTS.find((layout) => layout.id === gridLayout) || GRID_LAYOUTS[0]).cols;

  // Keep the cart visible whenever this becomes a desktop layout. On tablets
  // and phones it remains an on-demand drawer so product selection has room.
  useEffect(() => {
    setOpen(isCartPersistent);
  }, [isCartPersistent]);
  // On mobile/tablet the cart is a full-width overlay, so once "Save and
  // Continue" opens the payment screen the cart would sit on top of it and the
  // tap would look like it did nothing. Close it so PaymentDetails is visible.
  // Desktop keeps the cart docked alongside, which is the point of it there.
  useEffect(() => {
    if (isPaymentView && !isCartPersistent) {
      setOpen(false);
    }
  }, [isPaymentView, isCartPersistent]);

  const handleDrawerClose = () => {
    setOpen(false);
  };
  const { invoice ,invoices} = useSelector((state) => state.invoices);
  const  staffCommissionDetails  = useSelector(
    (state) => state.staffCommissionDetails.staffCommissionDetails
  );
  const {
    cartItems,
    setCartItems,
    clearInvoiceStockCredit,
    vat,
    isCartSubmitting,
    getTotalPrice,
  } = useCart();
  const { discountVal, clearDiscount } = useDiscount();
  const { clearActiveSession } = useOrderSession();
  const prevPathRef = useRef(location.pathname);
  const isEditMode = location.pathname.startsWith("/edit/invoice");

  // When navigating away from an invoice edit route (/edit/invoice/:id) to another page
  // or a different invoice, discard any lingering cart items, stock credits, discounts,
  // or Redux invoice data so future POS visits or edits start completely clean.
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    prevPathRef.current = currentPath;

    const wasEditing = prevPath.startsWith("/edit/invoice");
    const isStillEditing = currentPath.startsWith("/edit/invoice");
    const prevId = prevPath.split("/")[3];
    const currentId = currentPath.split("/")[3];

    if (wasEditing && (!isStillEditing || prevId !== currentId)) {
      setCartItems([]);
      clearInvoiceStockCredit?.();
      clearDiscount?.();
      clearActiveSession?.();
      dispatch(clearInvoice());
    }
  }, [location.pathname, setCartItems, clearInvoiceStockCredit, clearDiscount, clearActiveSession, dispatch]);

  const isInvoiceDirty = useMemo(() => {
    if (!isEditMode || !id || !invoice || String(invoice._id) !== String(id) || isCartSubmitting) {
      return false;
    }
    const originalItems = invoice.items || [];
    if (originalItems.length !== cartItems.length) {
      return true;
    }
    for (let i = 0; i < cartItems.length; i++) {
      const ci = cartItems[i];
      const ciId = String(ci._id || ci.id || "");
      const oi = originalItems.find((line) => {
        const lineId = String(line._id || line.item?._id || line.item || "");
        return lineId === ciId;
      });
      if (!oi) return true;
      if (Math.abs(Number(ci.quantity || 0) - Number(oi.quantity || 0)) > 0.0001) return true;
      const baseRate = Number(
        oi.rate ??
        oi.originalRate ??
        oi.item?.units?.sale?.price ??
        oi.item?.rate ??
        0
      );
      const currRate = Number(ci.overridePrice ?? ci.rate ?? baseRate);
      if (Math.abs(currRate - baseRate) > 0.0001) return true;
      if ((ci.remark || "").trim() !== (oi.remark || "").trim()) return true;
    }
    const origDiscount = Number(invoice.discount || 0);
    const currDiscount = Number(discountVal || 0);
    if (Math.abs(origDiscount - currDiscount) > 0.0001) return true;

    const origVat = Number(invoice.vat || 0);
    const currVat = Number(vat || 0);
    if (Math.abs(origVat - currVat) > 0.0001) return true;

    return false;
  }, [isEditMode, id, invoice, cartItems, discountVal, vat, isCartSubmitting]);

  const hasNewSaleWork = !isEditMode && cartItems.length > 0;
  const isSaleDirty =
    isActive && !isCartSubmitting && (isInvoiceDirty || hasNewSaleWork);

  //console.log(staffCommissionDetails);
  useEffect(() => {
    // Since this screen now stays mounted while hidden on other routes,
    // skip refetching for the render where it's becoming inactive - only
    // do it on mount and on each return to an active POS route.
    if (!isActive) return;
    if(id){
      dispatch(getInvoice(id));
    }else{
      // Fresh POS session (plain /PosMainScreen, no :id) must not inherit
      // whatever invoice was last loaded via /invoice/:id or /edit/invoice/:id.
      dispatch(clearInvoice());
      dispatch(getInvoices());
    }

    dispatch(getStaffCommissionDetail(id));
    // eslint-disable-next-line
  }, [id, isActive]);

  return (
    <div className={`${classes.root}${isPaymentView ? "" : ` ${classes.rootWithCategories}`}`}>
      <UnsavedChangesGuard
        when={isSaleDirty}
        message={
          isEditMode
            ? "You have unsaved changes on this invoice. Are you sure you want to leave without saving?"
            : "You have unsaved items on this sale. Are you sure you want to leave without saving?"
        }
        details={{
          docNumber: isEditMode
            ? formatInvoiceNumber(invoice?.invoiceNumber) || "Invoice"
            : "New Sale",
          partyType: "Customer",
          partyName: invoice?.client?.name || "Cash Customer",
          amount: isEditMode ? invoice?.total : getTotalPrice(),
          extra: isEditMode
            ? "All unsaved modifications will be discarded."
            : "Items in the cart will be discarded.",
        }}
        allowPath={(nextLocation) => {
          const next = nextLocation.pathname;
          if (
            location.pathname.startsWith("/PosMainScreen") &&
            next.startsWith("/PosMainScreen")
          ) {
            return true;
          }
          if (id && next.startsWith(`/edit/invoice/${id}`)) {
            return true;
          }
          return false;
        }}
      />
      <AppBarComp
        open={open}
        setOpen={setOpen}
        invoice={invoice}
        isCartPersistent={isCartPersistent}
        gridLayout={gridLayout}
        onGridLayoutChange={handleGridLayoutChange}
      />
      {isPaymentView ? (
        <PaymentDetails invoiceId={id} />
      ) : (
        <ItemCards
          staffCommissionDetails={staffCommissionDetails}
          cartOpen={open}
          isCartPersistent={isCartPersistent}
          isActive={isActive}
          gridCols={currentGridCols}
        />
      )}
      <ShiftStartDialog />
      <Cart
        open={open}
        handleDrawerClose={handleDrawerClose}
        currency={currency}
        invoice={invoice}
        invoices={invoices}
        isCartPersistent={isCartPersistent}
      />
      {!isPaymentView && <FloatingFooter open={open} isCartPersistent={isCartPersistent} />}
      <FabButton />
    </div>
  );
}

export default PosMainScreen;
